import {
  ASSESSMENT_REQUIRED_FIELDS,
  NUMBER_FIELDS,
  REQUIRED_FIELDS_ERROR_MESSAGES,
  UNIQUE_FIELDS,
  VISIT_REQUIRED_FIELDS,
} from "../soe/constants";
import { isEmpty, isNumberNotEmpty } from "../utils/helpers";

const ERRORS = {
  UNABLE_TO_SAVE_SITE_WITHOUT_EFFECTIVE_DATE: "Unable to save visits with cost without site contract effective date.",
  PROVIDE_DATES_FOR_SITES: "Please provide site contract effective date for the following sites:",
  SITE_WITHOUT_DATE: (siteName) => `• ${siteName}`,
}

/**
 * Checks if a value is unique in a list by comparing a specific key
 * @param {Array} list - The list of objects to check
 * @param {string} key - The key to compare
 * @param {*} value - The value to check for uniqueness
 * @param {number} index - The current index to exclude from comparison
 * @returns {boolean} - True if the value is unique
 */
export const isUnique = (list, key, value, index) => {
  return !list.filter((l, i) => l[key] === value && !(index === i)).length;
};

/**
 * Validates a field based on its type and requirements
 * @param {string} key - The field key
 * @param {*} value - The field value
 * @param {number} index - The index in the list
 * @param {Array} visits - The list of visits
 * @param {Array} assessments - The list of assessments
 * @returns {boolean} - True if the field is valid
 */
export const fieldValid = (key, value, index, visits, assessments) => {
  if (NUMBER_FIELDS[key]) {
    return isNumberNotEmpty(value);
  } else if (UNIQUE_FIELDS[key]) {
    switch (key) {
      case "visitName":
        return !value
          ? false
          : isUnique(visits, "visitName", value, index);
      case "assessmentName":
        return !value
          ? false
          : isUnique(assessments, "assessmentName", value, index);
      default:
        break;
    }
  } else if (!value) return false;
  return true;
};

/**
 * Adds or increments an error message
 * @param {Object} errorMessages - Current error messages object
 * @param {string} key - The field key
 * @param {string|Function} message - The error message or message function
 * @param {Object} [context] - Optional context for message functions
 * @returns {Object} - Updated error messages object
 */
const addErrorMessage = (errorMessages, key, message, context = {}) => {
  const resolvedMessage = typeof message === "function" 
    ? message(context.visitIntervalType || "Day")
    : message;

  return {
    ...errorMessages,
    [key]: {
      message: resolvedMessage,
      count: errorMessages[key]?.count ? errorMessages[key].count + 1 : 1,
    },
  };
};

/**
 * Validates a single field and adds error if invalid
 * @param {Object} params - Validation parameters
 * @returns {Object} - Updated error messages object
 */
const validateField = ({
  errorMessages,
  key,
  value,
  index,
  requiredFields,
  visits,
  assessments,
  shouldSkip = false,
  context = {},
}) => {
  if (shouldSkip || !requiredFields[key]) {
    return errorMessages;
  }

  const isValid = fieldValid(key, value, index, visits, assessments);
  
  if (!isValid) {
    return addErrorMessage(
      errorMessages,
      key,
      REQUIRED_FIELDS_ERROR_MESSAGES[key],
      context
    );
  }

  return errorMessages;
};

/**
 * Validates all assessments
 * @param {Array} assessments - List of assessments to validate
 * @param {Array} visits - List of visits for reference
 * @returns {Object} - Error messages object
 */
const validateAssessments = (assessments, visits) => {
  let errorMessages = {};

  assessments.forEach((assessment, index) => {
    Object.keys(assessment).forEach((key) => {
      errorMessages = validateField({
        errorMessages,
        key,
        value: assessment[key],
        index,
        requiredFields: ASSESSMENT_REQUIRED_FIELDS,
        visits,
        assessments,
      });
    });
  });

  return errorMessages;
};

/**
 * Validates all visits
 * @param {Array} visits - List of visits to validate
 * @param {Array} assessments - List of assessments for reference
 * @param {Object} currentSOE - Current schedule of events object
 * @returns {Object} - Error messages object
 */
const validateVisits = (visits, assessments, currentSOE) => {
  let errorMessages = {};

  visits.forEach((visit, index) => {
    Object.keys(visit).forEach((key) => {
      const shouldSkipVisitStudyDay = (key === "visitStudyDay") && (visit.visitCategory === "Unscheduled");
      errorMessages = validateField({
        errorMessages,
        key,
        value: visit[key],
        index,
        requiredFields: VISIT_REQUIRED_FIELDS,
        visits,
        assessments,
        shouldSkip: shouldSkipVisitStudyDay,
        context: currentSOE,
      });
    });
  });

  return errorMessages;
};

const validateVisitsWithCost = (visitsHasCost, SOECountries) => {
  let errorMessages = {};
  let siteNames = [];
  for (const key in visitsHasCost) {
    const visitWithCost = visitsHasCost[key];
    if (visitWithCost?.hasCost && 
      visitWithCost?.siteId !== 1 &&
      isEmpty(visitWithCost?.siteContractEffectiveDate)
    ) {
      const siteName = SOECountries?.find((country) =>
        country?.siteGroupId === visitWithCost?.siteGroupId &&
        country?.siteId === visitWithCost?.siteId
      )?.dropDownListLabel;

      if (siteName) {
        siteNames.push(siteName);
      }
    }
  }

  if (siteNames.length > 0) {
    const bulletList = siteNames.map(name => ERRORS.SITE_WITHOUT_DATE(name)).join('\n');
    const fullMessage = `${ERRORS.UNABLE_TO_SAVE_SITE_WITHOUT_EFFECTIVE_DATE}\n${ERRORS.PROVIDE_DATES_FOR_SITES}\n${bulletList}`;
    
    errorMessages = {
      ...errorMessages,
      siteContractEffectiveDate: {
        message: fullMessage,
        count: siteNames.length,
      },
    };
  }
  
  return errorMessages;
};

/**
 * Validates the schedule of events table
 * @param {Object} params - The validation parameters
 * @param {Array} params.scheduleOfEventsAssessments - The list of assessments
 * @param {Array} params.scheduleOfEventsVisits - The list of visits
 * @param {Object} params.visitsHasCost - Object indicating which visits have cost
 * @param {Object} params.currentSOE - The current schedule of events object
 * @param {Object} params.specialSOEErrorMessages - Special error messages
 * @param {Function} params.setErrorMessages - Function to set error messages (optional)
 * @returns {Array} - [isValid, errorMessages]
 */
export const validateSOE = ({
  scheduleOfEventsAssessments,
  scheduleOfEventsVisits,
  visitsHasCost,
  SOECountries,
  currentSOE,
  specialSOEErrorMessages,
  setErrorMessages,
}) => {
  // Validate assessments and visits
  const assessmentErrors = validateAssessments(
    scheduleOfEventsAssessments,
    scheduleOfEventsVisits
  );
  
  const visitErrors = validateVisits(
    scheduleOfEventsVisits,
    scheduleOfEventsAssessments,
    currentSOE
  );

  const visitsWithCostErrors = validateVisitsWithCost(visitsHasCost, SOECountries);

  // Merge all error messages
  const allErrorMessages = {
    ...assessmentErrors,
    ...visitErrors,
    ...visitsWithCostErrors,
    ...specialSOEErrorMessages,
  };

  // Update error messages state if setter is provided
  if (setErrorMessages) {
    setErrorMessages(allErrorMessages);
  }

  // Check if valid
  const isValid = Object.keys(allErrorMessages).length === 0;

  return [isValid, allErrorMessages];
};
